/**
 * useGeminiWithOpenAiFallback — the active transport on Gemini-capable binaries.
 *
 * Runs the Gemini interpreter as primary, but if Gemini can't establish the
 * session (weak signal, token/WS failure — MAX_CONSECUTIVE_FAILURES exhausted)
 * it transparently falls back to the OpenAI/WebRTC transport for the SAME start
 * request. WebRTC tolerates the variable wifi in patient homes far better
 * (ICE/STUN/renegotiation), so this is the safety net a Gemini-only build
 * otherwise lacked — before this, a Gemini connect failure just showed
 * "Translation couldn't start" with no second path to try.
 *
 * Both underlying hooks are called unconditionally (rules of hooks); only ONE
 * is ever connected at a time — the inactive one sits idle. This file is loaded
 * via a conditional require in useActiveLiveTranslationSession, so an old binary
 * without the native audio module never imports it (nor react-native-audio-api).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type { ModalStatus, OpenSessionInput } from "./types";
import { useGeminiLiveTranslationSession } from "./useGeminiLiveTranslationSession";
import { useLiveTranslationSession, type SessionHandle } from "./useLiveTranslationSession";

import { captureSentryEvent } from "@/lib/sentry-init";

type StartInput = OpenSessionInput & { targetLanguage: string };

export function useGeminiWithOpenAiFallback(): SessionHandle {
    const gemini = useGeminiLiveTranslationSession();
    const openai = useLiveTranslationSession();

    const [active, setActive] = useState<"gemini" | "openai">("gemini");
    const lastInputRef = useRef<StartInput | null>(null);
    // True once we've dropped to OpenAI for the current start request, so we
    // fall back at most once per attempt (no ping-pong if OpenAI also fails).
    const fellBackRef = useRef(false);

    // Gemini exhausted its retries → transparently switch to the OpenAI/WebRTC
    // transport with the same target. Fires at most once per start (fellBackRef).
    useEffect(() => {
        if (
            active === "gemini" &&
            gemini.status === "error" &&
            !fellBackRef.current &&
            lastInputRef.current
        ) {
            fellBackRef.current = true;
            const input = lastInputRef.current;
            captureSentryEvent({
                message: "live-translation: gemini connect failed, falling back to openai",
                level: "warning",
                tags: { feature: "live-translation", from: "gemini", to: "openai" },
                extra: {
                    visitId: input.visitId,
                    targetLanguage: input.targetLanguage,
                    geminiError: gemini.errorMessage ?? undefined
                }
            });
            gemini.reset();
            setActive("openai");
            void openai.start(input);
        }
        // gemini/openai identities change every render; key the effect on the
        // status edge + `active` so it fires exactly on the gemini→error edge
        // (intentionally not depending on the hook objects themselves).
    }, [active, gemini.status]);

    // While the fallback is mid-flight, present "connecting" instead of Gemini's
    // transient "error" so the failure dialog doesn't flash before OpenAI starts.
    const fallbackPending =
        active === "gemini" &&
        gemini.status === "error" &&
        !fellBackRef.current &&
        lastInputRef.current != null;

    const activeSession = active === "gemini" ? gemini : openai;
    const status: ModalStatus = fallbackPending ? "connecting" : activeSession.status;

    const start = useCallback(
        async (input: StartInput) => {
            lastInputRef.current = input;
            fellBackRef.current = false;
            // Reset both transports, then start Gemini fresh — reset clears any
            // stale consecutive-failure count on the Gemini hook from a prior try.
            openai.reset();
            gemini.reset();
            setActive("gemini");
            await gemini.start(input);
        },
        [gemini, openai]
    );

    const stop = useCallback(async () => {
        // Only the active transport holds a connection; stopping the idle one is
        // a harmless no-op.
        await Promise.allSettled([gemini.stop(), openai.stop()]);
    }, [gemini, openai]);

    const reset = useCallback(() => {
        fellBackRef.current = false;
        lastInputRef.current = null;
        gemini.reset();
        openai.reset();
        setActive("gemini");
    }, [gemini, openai]);

    return {
        status,
        sessionId: activeSession.sessionId,
        targetLanguage: activeSession.targetLanguage,
        turns: activeSession.turns,
        durationMs: activeSession.durationMs,
        errorMessage: fallbackPending ? null : activeSession.errorMessage,
        start,
        stop,
        reset
    };
}
